import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Phone, Clock, ExternalLink, Radio } from "lucide-react";
import { timeAgo } from "@/lib/time";
import { LiveMenuBadge } from "@/components/LiveMenuBadge";
import { DispensaryCover } from "@/components/DispensaryCover";
import { isTrustedImageUrl } from "@/lib/imageTrust";
import { externalLinkProps } from "@/lib/externalLink";
import { FavoriteStoreButton } from "@/components/FavoriteStoreButton";
import { DispensaryMenuFilters } from "@/components/DispensaryMenuFilters";
import { Button } from "@/components/ui/button";
import { trackDispensaryView } from "@/lib/analytics";
import { AskDispensaryDialog } from "@/components/AskDispensaryDialog";
import { MessageSquarePlus, Tag } from "lucide-react";
import { paletteFor } from "@/lib/chainPalette";
import { GoogleReviewsBlock } from "@/components/GoogleReviewsBlock";
import { ReviewsBlock } from "@/components/ReviewsBlock";
import { FeaturedAtTrulieveRow } from "@/components/FeaturedAtTrulieveRow";
import { BrandFilterChips } from "@/components/BrandFilterChips";

const DEFAULT_FILTERS = {
  category: "",
  type: "",
  brand: "",
  thc_min: 0,
  price_max: 200,
  in_stock_only: false,
  pickup: false,
  delivery: false,
  effects: [],
};

// In-page filter logic. We reuse the same shared FiltersBar component the
// global Search page uses, but apply it client-side against this shop's
// menu (no extra API call). Cross-shop concerns (distance, live_menu_only)
// are inert here — the UI still shows the sliders so the experience is
// consistent across the app, they just don't change a single-shop view.
const CATEGORY_ALIASES = {
  flower: "flower",
  vape: "vape", vapes: "vape", vaporizer: "vape", vaporizers: "vape",
  concentrate: "concentrate", concentrates: "concentrate",
  edible: "edible", edibles: "edible",
  tincture: "tincture", tinctures: "tincture",
  topical: "topical", topicals: "topical",
};
const normCat = (c) => {
  const k = (c || "").toLowerCase();
  return CATEGORY_ALIASES[k] || k;
};

const applyFilters = (menu, f) =>
  menu.filter((p) => {
    if (f.category && normCat(p.category) !== f.category) return false;
    if (f.type && (p.strain_type || "").toLowerCase() !== f.type) return false;
    if (f.brand && (p.brand || "").toLowerCase() !== f.brand.toLowerCase()) return false;
    if (f.thc_min && (p.thc_pct ?? 0) < f.thc_min) return false;
    if (f.price_max && f.price_max < 200 && (p.price ?? 0) > f.price_max) return false;
    if (f.in_stock_only && p.stock_status !== "in_stock") return false;
    if (Array.isArray(f.effects) && f.effects.length > 0) {
      const peffs = (p.effects || []).map((e) => (e || "").toLowerCase());
      if (!f.effects.every((e) => peffs.includes(e.toLowerCase()))) return false;
    }
    return true;
  });

export default function DispensaryDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [askOpen, setAskOpen] = useState(false);
  const [sales, setSales] = useState([]);

  useEffect(() => {
    setLoading(true);
    setFilters(DEFAULT_FILTERS);
    // Operator-funnel: log the open. trackDispensaryView is in-tab deduped.
    if (slug) trackDispensaryView(slug);
    api
      .get(`/dispensaries/${slug}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    // Auto-pulled sales banners (Trulieve today; more sources later).
    // Fire-and-forget — banner card just stays hidden if nothing's live.
    api
      .get(`/dispensaries/${slug}/sales`)
      .then((r) => setSales(r.data?.results || []))
      .catch(() => setSales([]));
  }, [slug]);

  const filteredMenu = useMemo(
    () => (data?.menu ? applyFilters(data.menu, filters) : []),
    [data, filters]
  );

  if (loading) {
    return (
      <main className="container-narrow py-10">
        <Skeleton className="h-72 rounded-2xl" />
      </main>
    );
  }
  if (!data) return null;
  const { dispensary, menu } = data;
  const palette = paletteFor({
    name: dispensary.name,
    slug: dispensary.slug,
    posHint: dispensary.pos_hint || dispensary.pos_detected,
  });
  // Subtle wash — lets the chain SVG breathe like it does on the list
  // cards (mostly open gradient up top, just enough darkening at the
  // bottom-edge band for the address row to read).
  const overlayStyle = {
    backgroundImage:
      `linear-gradient(to top, ${palette.bg1}cc 0%, ${palette.bg1}66 30%, ${palette.bg1}00 60%)`,
  };

  return (
    <main>
      <div className="relative h-64 sm:h-80 overflow-hidden">
        {isTrustedImageUrl(dispensary.cover_url) ? (
          <img
            src={dispensary.cover_url}
            alt={dispensary.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <DispensaryCover
            name={dispensary.name}
            slug={dispensary.slug}
            city={dispensary.city}
            posHint={dispensary.pos_hint || dispensary.pos_detected}
            showName={false}
            testId={`disp-cover-${dispensary.slug}`}
          />
        )}
        <div className="absolute inset-0" style={overlayStyle} />
        {/* Brand accent rule along the bottom of the cover */}
        <div
          className="absolute inset-x-0 bottom-0 h-[3px]"
          style={{
            backgroundImage: `linear-gradient(to right, ${palette.accent} 0%, ${palette.accent}00 70%)`,
          }}
        />
        <div className="absolute inset-x-0 bottom-0 container-narrow pb-8">
          <Link
            to="/dispensaries"
            data-testid="disp-back"
            className="text-[11px] uppercase tracking-[0.2em] hover:opacity-100 transition-opacity drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
            style={{ color: palette.ink, opacity: 0.8 }}
          >
            ← All dispensaries
          </Link>
          <div
            data-testid="disp-eyebrow"
            className="mt-1 text-[11px] uppercase tracking-[0.22em] drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
            style={{ color: palette.accent }}
          >
            ◆ {dispensary.city
              ? `${dispensary.city}, ${dispensary.state || ""}`.trim().replace(/,\s*$/, "")
              : (dispensary.state || "")}
          </div>
          <div className="flex items-end justify-between gap-3 mt-2">
            <h1
              data-testid="disp-title"
              className="font-serif text-3xl sm:text-5xl leading-[1.05] drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]"
              style={{ color: palette.ink }}
            >
              {dispensary.name}
            </h1>
            <FavoriteStoreButton
              slug={dispensary.slug}
              name={dispensary.name}
              size="lg"
            />
          </div>
          <div
            className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-sm drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
            style={{ color: palette.ink }}
          >
            <span className="flex items-center gap-1.5">
              <MapPin
                className="w-3.5 h-3.5"
                style={{ color: palette.accent }}
              />{" "}
              {dispensary.address}
            </span>
            <span className="flex items-center gap-1.5">
              <Phone
                className="w-3.5 h-3.5"
                style={{ color: palette.accent }}
              />{" "}
              {dispensary.phone}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock
                className="w-3.5 h-3.5"
                style={{ color: palette.accent }}
              />{" "}
              {dispensary.hours}
            </span>
          </div>
        </div>
      </div>

      <div className="container-narrow py-10">
        <div className="flex flex-wrap items-center gap-3 mb-10">
          {dispensary.pickup && (
            <span
              className="pill"
              style={{
                borderColor: `${palette.accent}55`,
                color: palette.accent,
                background: `${palette.bg2}1a`,
              }}
            >
              Pickup available
            </span>
          )}
          {dispensary.delivery && (
            <span
              className="pill"
              style={{
                borderColor: `${palette.accent}55`,
                color: palette.accent,
                background: `${palette.bg2}1a`,
              }}
            >
              Delivery
            </span>
          )}
          {dispensary.featured && (
            <span className="pill pill-primary">Featured partner</span>
          )}
          {dispensary.live_menu && <LiveMenuBadge size="md" />}
          {dispensary.coming_soon && (
            <span
              data-testid="disp-coming-soon"
              className="pill bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
            >
              Sunnyside — Coming soon
            </span>
          )}
          {dispensary.order_base_url && !dispensary.coming_soon && (
            <a
              {...externalLinkProps(dispensary.order_base_url)}
              data-testid="disp-order-page"
              className="ml-auto h-10 px-5 inline-flex items-center rounded-full text-xs font-medium transition-colors"
              style={{
                background: palette.accent,
                color: palette.bg1,
              }}
            >
              Visit ordering page <ExternalLink className="w-3 h-3 ml-1.5" />
            </a>
          )}
        </div>

        <h2 className="font-serif text-3xl mb-1">Live menu</h2>
        <p className="text-sm text-muted-foreground mb-6 flex items-center gap-2">
          <span>
            {filteredMenu.length} of {menu.length} product
            {menu.length === 1 ? "" : "s"} on the shelf
          </span>
          {dispensary.menu_updated_at && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span
                data-testid="menu-freshness"
                className="inline-flex items-center gap-1.5 text-emerald-300"
              >
                <Radio className="w-3 h-3 animate-pulse" strokeWidth={2.2} />
                Updated {timeAgo(dispensary.menu_updated_at)}
              </span>
            </>
          )}
        </p>

        {/* Auto-pulled sales banners — Trulieve specials feed today, more
            sources later. Hidden when the shop has no active sales. */}
        {sales.length > 0 && (
          <div
            className="mb-8 rounded-2xl border border-[#d4a268]/30 bg-gradient-to-br from-[#d4a268]/10 to-transparent p-5"
            data-testid="shop-sales-banner"
          >
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-[#d4a268]" />
                <h3 className="font-serif text-xl">
                  Live sales · {sales.length}
                </h3>
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-foreground/50">
                Auto-pulled — no shop input needed
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {sales.slice(0, 12).map((s) => (
                <Link
                  key={s.id}
                  to={`/sales/${s.id}`}
                  data-testid={`shop-sale-${s.external_id}`}
                  className="rounded-xl bg-background/40 border border-border/40 px-3 py-2.5 text-sm hover:bg-background/70 hover:border-emerald-500/40 transition-colors"
                >
                  <div className="font-medium text-foreground leading-snug">
                    {s.headline}
                  </div>
                  {s.ends_at && (
                    <div className="text-[10px] uppercase tracking-wider text-foreground/45 mt-1">
                      Ends {new Date(s.ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </div>
                  )}
                  <div className="text-[10px] text-emerald-300 mt-1.5 uppercase tracking-wider">
                    Shop this sale →
                  </div>
                </Link>
              ))}
            </div>
            {sales.length > 12 && (
              <div className="text-xs text-foreground/50 mt-3 text-center">
                + {sales.length - 12} more
              </div>
            )}
          </div>
        )}

        {/* "Ask this shop to fully join" CTA — only renders when the shop
            isn't on a live partner-API feed yet. Lets patients vote with
            their click; submissions surface in admin OutreachTab. */}
        {!dispensary.live_menu && !dispensary.coming_soon && (
          <div
            className="mb-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3 flex-wrap"
            data-testid="ask-shop-banner"
          >
            <MessageSquarePlus className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
            <div className="text-sm text-foreground/80 leading-relaxed flex-1 min-w-[200px]">
              Want <strong className="text-foreground">{dispensary.name}</strong>{" "}
              fully connected? Live menu, real-time stock, sales, and best-price-near-you.
            </div>
            <Button
              data-testid="ask-shop-cta"
              onClick={() => setAskOpen(true)}
              className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400 h-9 px-5 text-xs"
            >
              Ask them to join
            </Button>
          </div>
        )}

        {menu.length === 0 ? (
          <div className="border border-dashed border-border rounded-2xl p-16 text-center text-muted-foreground">
            {dispensary.coming_soon ? (
              <>
                {/* Commented out per user request (Trulieve-only mode) —
                    nothing deleted; re-enable by uncommenting the JSX below.
                <div className="font-serif text-2xl text-foreground mb-2">
                  Sunnyside is coming to {dispensary.city || "this location"}.
                </div>
                <p className="text-sm">
                  Cresco Labs is rebranding this storefront to Sunnyside. We&apos;ll
                  surface live menus + ordering the moment Sunnyside provisions
                  this location on sunnyside.shop.
                </p>
                */}
                <div className="font-serif text-2xl text-foreground mb-2">
                  Menu coming soon.
                </div>
                <p className="text-sm">
                  We&apos;ll surface the live menu the moment this location publishes it.
                </p>
              </>
            ) : (
              <>Menu coming soon.</>
            )}
          </div>
        ) : (
          <>
            <FeaturedAtTrulieveRow menu={menu} sales={sales} />
            <BrandFilterChips
              menu={menu}
              value={filters.brand}
              onChange={(b) => setFilters((f) => ({ ...f, brand: b }))}
            />
            <DispensaryMenuFilters
              menu={menu}
              filters={filters}
              setFilters={setFilters}
              onReset={() => setFilters(DEFAULT_FILTERS)}
              total={menu.length}
              matched={filteredMenu.length}
            />
            {filteredMenu.length === 0 ? (
              <div className="border border-dashed border-border rounded-2xl p-16 text-center">
                <div className="font-serif text-2xl mb-2">No matches</div>
                <p className="text-sm text-muted-foreground mb-4">
                  Try removing some filters to broaden the menu view.
                </p>
                <Button
                  data-testid="disp-filter-reset-empty"
                  variant="outline"
                  onClick={() => setFilters(DEFAULT_FILTERS)}
                  className="rounded-full border-border"
                >
                  Reset filters
                </Button>
              </div>
            ) : (
              <div
                key={JSON.stringify(filters)}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
              >
                {filteredMenu.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Google-side social proof + native community reviews. Both silently
            hide themselves when there's no content, so they never leave an
            awkward empty section on a brand-new shop page. */}
        <div className="mt-14 space-y-6">
          <GoogleReviewsBlock slug={dispensary.slug} />
          <ReviewsBlock kind="shops" slug={dispensary.slug} />
        </div>
      </div>
      <AskDispensaryDialog
        open={askOpen}
        onOpenChange={setAskOpen}
        defaultDispensaryName={dispensary.name}
        defaultDispensarySlug={dispensary.slug}
      />
    </main>
  );
}
